import { SocialChannel, SocialPublish } from "@/database";
import { getPlatformConfig } from "./platformConfig";
import { SOCIAL_PUBLISH_STATUS, PUBLISH_RANGE_TYPE } from "@/constants";

// The media details endpoint (getVodDetails, consumed by the Edit -> Sync ->
// Update flow) can return title/description as rich-text-authored HTML
// (<br />, <p>, etc.) - these platform destination fields are plain text
// inputs, not rich text, so pulling that HTML in as-is shows the literal
// tags to the user instead of the line breaks they represent. <br> variants
// become newlines; every other tag is stripped outright rather than kept as
// visible markup.
export const htmlTextToPlain = (value: string | null | undefined): string => {
  if (!value) return value ?? "";
  return value
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .trim();
};

// Duration validation functions
export function validateYouTubeDuration(
  durationInSeconds: number,
  isVerified = false
) {
  const maxDuration = isVerified ? 12 * 60 * 60 : 15 * 60;
  if (durationInSeconds > maxDuration) {
    throw new Error(
      `YouTube: Max duration is ${isVerified ? "12 hours" : "15 minutes"}`
    );
  }
}

export function validateFacebookDuration(durationInSeconds: number) {
  if (durationInSeconds < 1)
    throw new Error("Facebook: Video must be at least 1 second long");
  if (durationInSeconds > 240 * 60)
    throw new Error("Facebook: Max duration is 4 hours");
}

export function validateInstagramDuration(
  durationInSeconds: number,
  _type = "feed"
) {
  if (durationInSeconds < 3)
    throw new Error("Instagram: Video must be at least 3 seconds long");

  // const limits: Record<string, number> = {
  //   feed: 60,
  //   reel: 90,
  //   story: 15, // per segment
  //   app_feed: 15 * 60, // app max
  // };
  // const max = limits[_type] || limits.feed;
  // if (durationInSeconds > max) {
  //   throw new Error(`Instagram ${_type}: Max duration is ${max} seconds`);
  // }
}

export function validateLinkedInDuration(durationInSeconds: number) {
  if (durationInSeconds < 3)
    throw new Error("LinkedIn: Video must be at least 3 seconds long");
  if (durationInSeconds > 600)
    throw new Error("LinkedIn: Max duration is 10 minutes");
}

export function validateTwitterDuration(
  durationInSeconds: number,
  isBlueVerified = false
) {
  const maxDuration = isBlueVerified ? 2 * 60 * 60 : 140;
  if (durationInSeconds < 1)
    throw new Error("X: Video must be at least 1 second long");
  if (durationInSeconds > maxDuration) {
    throw new Error(
      `X: Max duration is ${
        isBlueVerified ? "2 hours" : "2 minutes 20 seconds"
      }`
    );
  }
}

// Generic duration validator that calls the appropriate platform-specific function
export function validateVideoDuration(
  platform: string,
  durationInSeconds: number,
  options?: {
    isVerified?: boolean;
    isBlueVerified?: boolean;
    type?: string;
  }
) {
  try {
    switch (platform.toLowerCase()) {
      case "youtube":
        validateYouTubeDuration(
          durationInSeconds,
          options?.isVerified || false
        );
        break;
      case "facebook":
        validateFacebookDuration(durationInSeconds);
        break;
      case "instagram":
        validateInstagramDuration(durationInSeconds, options?.type || "feed");
        break;
      case "linkedin":
        validateLinkedInDuration(durationInSeconds);
        break;
      case "twitter":
        validateTwitterDuration(
          durationInSeconds,
          options?.isBlueVerified || false
        );
        break;
      default:
        // For unknown platforms, don't throw error
        return true;
    }
    return true;
  } catch (error) {
    return error instanceof Error
      ? error.message
      : "Unknown duration validation error";
  }
}

/* ── Embedded media-edit social-publish helpers ── */

// A connected YouTube channel may not exist for a playlist's linked channel (e.g. with mock
// playlist data, or a channel the user hasn't connected). Synthesize a minimal one so the
// playlist-driven chip still appears with sensible YouTube defaults.
export const synthesizeYoutubeChannel = (
  channelId: string,
  channelName: string
): SocialChannel =>
  ({
    id: channelId,
    ct: 0,
    mt: 0,
    user: "",
    channel: channelId,
    platform: "youtube",
    name: channelName || channelId,
    image: "",
    status: 1,
    customUrl: "",
    title: "",
    desc: "",
    tags: "",
    pre: "",
    post: "",
    privacy: "private",
    is_sftp: 1,
    category: "News & Politics",
    monetization: "default",
    right_management: "yes",
    kids: false,
  }) as SocialChannel;

export const EDITED_FIELD_KEYS = [
  "title",
  "desc",
  "tags",
  "thumbnail",
] as const;
export type EditedFieldKey = (typeof EDITED_FIELD_KEYS)[number];

const editedFieldToWire = (f: EditedFieldKey): string =>
  f === "thumbnail" ? "thumb" : f;
const wireToEditedField = (w: string): EditedFieldKey | null =>
  w === "thumb"
    ? "thumbnail"
    : (EDITED_FIELD_KEYS as readonly string[]).includes(w)
      ? (w as EditedFieldKey)
      : null;

export const editedFieldsToArray = (
  editedFields: Partial<Record<EditedFieldKey, boolean>> | undefined | null
): string[] =>
  editedFields
    ? EDITED_FIELD_KEYS.filter((f) => editedFields[f]).map(editedFieldToWire)
    : [];

export const editedArrayToFields = (
  edited: unknown
): Partial<Record<EditedFieldKey, boolean>> => {
  const out: Partial<Record<EditedFieldKey, boolean>> = {};
  if (Array.isArray(edited)) {
    for (const w of edited) {
      const f = typeof w === "string" ? wireToEditedField(w) : null;
      if (f) out[f] = true;
    }
  }
  return out;
};

// A never-submitted destination chip (born {isNew:100}).
export const isDraftPublish = (d: any): boolean => d?.isNew === 100;

export const isOverridablePublish = (d: any): boolean =>
  isDraftPublish(d) || d?.status === 0;

export const isPreActivationPublish = (d: any): boolean =>
  isDraftPublish(d) ||
  d?.status === SOCIAL_PUBLISH_STATUS.QUEUE ||
  d?.status === SOCIAL_PUBLISH_STATUS.DRAFT;

// A destination still blocks "Save & Publish" when it was never submitted (isNew===100) or it
// failed (status < 0, needs a retry). Queued/in-progress/completed are all "being published" → OK.
export const isPendingPublish = (d: any): boolean =>
  isDraftPublish(d) || (typeof d?.status === "number" && d.status < 0);

export const buildDefaultPublishPayload = (data: any): any => {
  const platformType = data?.job_type || data?.platform || "linkedin";
  const config = getPlatformConfig(platformType);
  const values = {
    id: data?.id,
    platform: data?.id,
    channel_name: data?.channel_name ?? "",
    title: data?.title ?? "",
    desc: data?.desc ?? "",
    tags: Array.isArray(data?.tags) ? data.tags : [],
    thumb: data?.thumb ?? "",
    thumbId: data?.thumb_id ?? "",
    privacy: data?.privacy ?? config.defaultPrivacy,
    schedule: data?.schedule,
    category: data?.category ?? "",
    monetize_policy_id: data?.monetize_policy_id ?? "",
    monetize_policy_name: data?.monetize_policy_name ?? "",
    playlist: data?.playlist ?? "",
    is_created_for_kids: data?.is_created_for_kids ?? "no",
    is_rights_managements_allowed: data?.is_rights_managements_allowed ?? "",
  };

  return config.buildPayload(values, data, {
    title: true,
    desc: true,
    tags: true,
    privacy: true,
    category: true,
    monetize_policy_id: true,
    playlist: true,
    is_created_for_kids: true,
    is_rights_managements_allowed: true,
    schedule_after: true,
  });
};

export const validatePublishPayload = (data: any): string | null => {
  const platformType = data?.job_type || data?.platform || "linkedin";
  const config = getPlatformConfig(platformType);
  const label = data?.channel_name || platformType;

  const title = (data?.title ?? "").trim();
  if (!title) return `${label}: Title is required.`;
  if (!config.validateTitle(title))
    return `${label}: Title exceeds the platform limit.`;

  if (config.showDescription) {
    const desc = (data?.desc ?? "").trim();
    if (!desc) return `${label}: Description is required.`;
    if (config.validateDescription && !config.validateDescription(desc))
      return `${label}: Description exceeds the platform limit.`;
  }

  const thumb = data?.thumb ?? "";
  const thumbId = data?.thumb_id ?? "";
  if (!thumb || !thumbId) return `${label}: Thumbnail is required.`;

  if (platformType === "youtube") {
    if (!data?.category) return `${label}: Category is required.`;
    if (!data?.is_created_for_kids)
      return `${label}: "Created for kids" is required.`;
    const isSftp = !!(data?.isSFTPEnabled ?? data?.is_sftp);
    if (isSftp) {
      if (!data?.monetize_policy_id)
        return `${label}: Monetization is required.`;
      if (!data?.is_rights_managements_allowed)
        return `${label}: Rights management is required.`;
    }
  }

  return null;
};

// Human-readable tooltip for a published/queued destination chip's status indicator.
export const statusTooltip = (data: any): string => {
  switch (data?.status) {
    case 10:
      return "Published to this channel.";
    case -1:
    case -2:
      return data?.msg || "Publishing failed — open to retry.";
    case 0:
      return "In queue — upload begins once the video is active.";
    case 12:
      return "Draft — not marked active. Please mark active to Publish.";
    default:
      return "Publishing in progress…";
  }
};

/* ── Republish-to-same-channel: per-channel publish history ── */

// A record counts toward a channel's publish history as soon as it's been
// submitted at all - i.e. it actually exists in currentMediaDetails.socials
// (ES), not just as a local, never-submitted draft chip (isNew === 100,
// which never reaches this array in the first place). Queued (0) and draft
// (12) are still real, claimed submissions for that clip/segment/full slot -
// only a failed attempt (status < 0) frees the slot back up, since it never
// actually took.
const isLivePublishRecord = (item: any): boolean =>
  !(typeof item?.status === "number" && item.status < 0);

// The Slike channel-connection id is the one stable identity that ties a
// picker-side `SocialChannel` to its published `SocialPublish` records. A
// raw `SocialPublish` record stores it in `platform` (see platformConfig's
// buildPayload: `platform: values.id`, where `values.id` is the connected
// channel's own id) - NOT in `channel_id`, which is a different,
// platform-specific external id (e.g. YouTube's own channel id) that only
// some platforms carry and isn't unique per Slike connection.
export const resolveChannelKey = (item: { platform: string }): string =>
  item.platform;

// A `SocialChannel` (the picker's own channel object) stores that same
// identity directly in `id`.
export const resolveChannelKeyFromChannel = (item: { id: string }): string =>
  item.id;

export const SEGMENT_BUFFER_SECONDS = 3;

export interface ChannelPublishHistory {
  channelKey: string;
  hasFullPublish: boolean;
  publishedClipIds: Set<string>;
  publishedSegments: Array<{ start: number; end: number }>;
  records: SocialPublish[];
}

// Build a per-channel ledger of everything already live for this media on
// this destination (full / which clips / which trimmed ranges), derived
// straight from `currentMediaDetails.socials` — the backend already returns
// every past publish record for a channel, so no extra fetch is needed.
export const buildChannelPublishHistory = (
  socials: SocialPublish[] | undefined | null,
  channelKey: string | undefined | null
): ChannelPublishHistory => {
  const records = (
    Array.isArray(socials) && channelKey
      ? socials.filter(
          (item: any) =>
            resolveChannelKey(item) === channelKey && isLivePublishRecord(item)
        )
      : []
  ) as SocialPublish[];

  const publishedClipIds = new Set<string>();
  const publishedSegments: Array<{ start: number; end: number }> = [];
  let hasFullPublish = false;

  for (const record of records) {
    const r = record as any;
    const publishType = r.publish_type || PUBLISH_RANGE_TYPE.FULL;
    if (publishType === PUBLISH_RANGE_TYPE.CLIPS) {
      if (r.publish_clip_id) publishedClipIds.add(r.publish_clip_id);
    } else if (publishType === PUBLISH_RANGE_TYPE.SEGMENT) {
      // The backend omits a falsy media_start (0) from the stored record
      // instead of persisting it as 0 - so a segment trimmed from the very
      // start of the video comes back with media_start missing entirely,
      // not 0. Since publishType is already known to be SEGMENT here, a
      // missing media_start unambiguously means "starts at 0", not "no
      // range was ever recorded" - defaulting it to 0 is what lets a
      // full-duration segment (0 -> duration) actually register in
      // publishedSegments instead of being silently dropped.
      if (r.media_end != null) {
        publishedSegments.push({
          start: r.media_start ?? 0,
          end: r.media_end,
        });
      }
    } else {
      hasFullPublish = true;
    }
  }

  return {
    channelKey: channelKey || "",
    hasFullPublish,
    publishedClipIds,
    publishedSegments,
    records,
  };
};

// True when [start, end] is a near-duplicate of an already-published
// segment for this channel — i.e. both its start AND its end fall within
// `bufferSeconds` of that segment's start/end. A range that only shares one
// boundary (e.g. published 5-30, new 12-18, or new 28-40) is a genuinely new
// range and is allowed; only republishing essentially the same range again
// is blocked.
//
// `extraRanges` lets callers fold in ranges history.publishedSegments can't
// see on its own — e.g. already-published Clips-type destinations, whose
// time range lives on the clip definition (component-local `availableClips`,
// keyed by `publishedClipIds`), not on the stored SocialPublish record.
export const segmentOverlapsHistory = (
  history: Pick<ChannelPublishHistory, "publishedSegments">,
  start: number,
  end: number,
  bufferSeconds: number = SEGMENT_BUFFER_SECONDS,
  extraRanges: Array<{ start: number; end: number }> = []
): boolean =>
  history.publishedSegments
    .concat(extraRanges)
    .some(
      (seg) =>
        Math.abs(start - seg.start) <= bufferSeconds &&
        Math.abs(end - seg.end) <= bufferSeconds
    );

// Resolves which "What to publish" mode a fresh republish attempt on an
// already-used channel should default into: skip Full if it's been done,
// skip Clips if every available clip is already published, land on Segment
// as the always-available fallback.
export const resolveNextPublishMode = (
  history: Pick<ChannelPublishHistory, "hasFullPublish" | "publishedClipIds">,
  availableClipIds: string[]
): (typeof PUBLISH_RANGE_TYPE)[keyof typeof PUBLISH_RANGE_TYPE] => {
  if (!history.hasFullPublish) return PUBLISH_RANGE_TYPE.FULL;
  const hasUnusedClip = availableClipIds.some(
    (id) => !history.publishedClipIds.has(id)
  );
  if (hasUnusedClip) return PUBLISH_RANGE_TYPE.CLIPS;
  return PUBLISH_RANGE_TYPE.SEGMENT;
};
